/**
 * VendorLayout — Premium layout matching Patient Portal visual language.
 * Responsive navigation with 3D icon tiles.
 */
import { SuperAdminBadge } from '@/shared/components/SuperAdminBadge';
import { PortalAccessGate } from '@/shared/components/PortalAccessGate';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Truck, LogOut, CalendarDays, Plane, PlaneTakeoff, Map, Hotel, Bell, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useVendorAuth } from '../hooks/useVendorAuth';
import { cn } from '@/lib/utils';
import InlineErrorBoundary from '@/components/system/InlineErrorBoundary';
import VendorIcon3D, { VENDOR_VARIANT_TINT, type VendorIconVariant } from './VendorIcon3D';
import { portalPath } from '@/shared/app/portalPath';
import { VENDOR_PORTAL_MODULES, type VendorPortalModule } from '@/shared/config/platform-config';

const NAV_DEFS: Array<{ path: string; label: string; icon: typeof Plane; variant: VendorIconVariant; module: VendorPortalModule }> = [
  { path: '/arrivals', label: 'Arrivals', icon: Plane, variant: 'arrival', module: 'arrivals' },
  { path: '/departures', label: 'Departures', icon: PlaneTakeoff, variant: 'departure', module: 'departures' },
  { path: '/daily', label: 'Daily Rental', icon: CalendarDays, variant: 'rental', module: 'daily' },
  { path: '/tours', label: 'Tours', icon: Map, variant: 'tour', module: 'tours' },
  { path: '/stays', label: 'Stays', icon: Hotel, variant: 'stay', module: 'stays' },
  { path: '/interpreters', label: 'Interpreters', icon: Languages, variant: 'success', module: 'interpreters' },
  { path: '/changes', label: 'Changes', icon: Bell, variant: 'neutral', module: 'changes' },
];

export default function VendorLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useVendorAuth();
  const p = portalPath('vendor');
  const NAV_ITEMS = NAV_DEFS.filter(n => VENDOR_PORTAL_MODULES[n.module]).map(n => ({ ...n, path: p(n.path) }));

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate(p('/login'));
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 sm:px-6 py-3.5 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <VendorIcon3D icon={Truck} variant="arrival" size="sm" />
          <SuperAdminBadge portal="vendor_portal" />
          <div>
            <h1 className="text-base font-bold text-foreground leading-tight">
              {profile?.isVendorMaster
                ? 'Vendor Master'
                : profile?.vendorId === 'pending_setup'
                  ? 'Vendor Portal'
                  : (profile?.vendorName || 'Vendor Portal')}
            </h1>
            {profile?.isVendorMaster ? (
              <p className="text-xs text-primary font-medium">Full Access • Internal Testing</p>
            ) : profile?.vendorId === 'pending_setup' ? (
              <p className="text-xs text-destructive font-medium">⚠ Setup Required</p>
            ) : (
              <p className="text-xs text-muted-foreground">{profile?.vendorName ? 'Vendor Portal' : 'Transport'}</p>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-1.5 text-muted-foreground rounded-xl">
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline text-sm">Logout</span>
        </Button>
      </header>

      {/* Navigation — responsive icon tiles */}
      <nav className="bg-card border-b border-border px-3 sm:px-6 py-2 sm:py-2.5">
        <div className="flex gap-1 sm:gap-1.5 overflow-x-auto scrollbar-none">
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.path;
            const tint = VENDOR_VARIANT_TINT[item.variant];

            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0',
                  active
                    ? 'bg-primary/5 text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                )}
              >
                {active ? (
                  <VendorIcon3D icon={item.icon} variant={item.variant} size="xs" />
                ) : (
                  <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center shrink-0', tint)}>
                    <item.icon className="h-3.5 w-3.5" />
                  </div>
                )}
                <span className="hidden sm:inline">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 p-3 sm:p-5 lg:p-6 max-w-7xl mx-auto w-full">
        <InlineErrorBoundary title="Vendor page crashed">
          <PortalAccessGate portal="vendor_portal"><Outlet /></PortalAccessGate>
        </InlineErrorBoundary>
      </main>
    </div>
  );
}
